import { FeeValueShape } from './FeeValueInput';

/**
 * TCX-867 — validate a Royalty / Advertising Fee `value` on submit, aware of
 * the selected `type`. Generalizes TCX-866's `validatePercentageValue`
 * (percent-only) to all three types:
 *
 *   - unchanged from what was hydrated (same type AND same value) — including
 *     a preserved non-numeric legacy value under `percent`/`currency_usd` —
 *     always passes, so the franchisor isn't blocked from saving an unrelated
 *     field on this form without first overwriting preserved content.
 *   - `open_text` is free text by definition and is never checked.
 *   - `percent`/`currency_usd`, on a NEW edit, must be a plain number (no
 *     letters or symbols) — matching the backend's `NUMERIC_VALUE_PATTERN`.
 *   - `percent` additionally must be within 0–100 (a frontend-only nicety;
 *     the backend does not bound `currency_usd`).
 *
 * Returns an error message, or `null` when the value is acceptable.
 */
export function validateFeeValue(
  current: FeeValueShape,
  original: FeeValueShape,
): string | null {
  const { type, value } = current;
  if (!value) return null;

  if (type === original.type && value === original.value) {
    return null;
  }

  if (type === 'open_text') return null;

  const raw = String(value);
  if (!/^\d*\.?\d*$/.test(raw) || !/\d/.test(raw)) {
    return 'Please enter a plain number (no letters or symbols)';
  }

  // The backend's isPlainNumericFeeValue also requires a FINITE number, for
  // currency as well as percent: a long enough digit string is Infinity and
  // would pass this form only to be refused with a 400 on save.
  if (!Number.isFinite(Number(raw))) {
    return 'Please enter a smaller number';
  }

  if (type === 'percent') {
    const numValue = parseFloat(raw);
    if (!Number.isFinite(numValue) || numValue < 0 || numValue > 100) {
      return 'Percentage must be between 0 and 100';
    }
  }

  return null;
}
